import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  Banknote, Building2, CheckCircle2, ChevronRight, DoorOpen, Handshake, KeyRound, Layers, Plus, Search, Timer, X,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { useCompanyCurrency, useLabels } from '@/contexts/WorkspaceContext';
import { useDebounced } from '@/hooks/useDebounced';
import { Chip, Segmented } from '@/pages/catalog/shared';
import {
  PropertiesSummary, PropertyListItem, PropertyUnit, UNIT_STATUSES, UNIT_TYPES, UnitStatus, UnitType, realEstateService,
} from '@/services/real-estate.service';
import PropertyFormModal from './PropertyFormModal';
import {
  Kpi, PROPERTY_TYPE_LABEL, PURPOSE_LABEL, UNIT_STATUS_LABEL, UNIT_STATUS_VARIANT, UNIT_TYPE_LABEL, formatDay, formatPrice,
  placeLine, unitPrices, unitSpecs,
} from './shared';

type View = 'units' | 'properties';
const LIMIT = 25;
const BEDROOM_FILTERS: Array<{ value: string; label: string }> = [
  { value: '0', label: 'Studio' }, { value: '1', label: '1 bed' }, { value: '2', label: '2 beds' },
  { value: '3', label: '3 beds' }, { value: '4', label: '4 beds' }, { value: '5+', label: '5+ beds' },
];

export default function PropertiesPage() {
  const t = useLabels();
  const navigate = useNavigate();
  const companyCurrency = useCompanyCurrency();
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'properties' ? 'properties' : 'units';
  const plural = t('properties');
  const noun = t('properties', 'singular');

  const [summary, setSummary] = useState<PropertiesSummary | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  // Units view filters (map 1:1 to ListUnitsDto).
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 300);
  const [status, setStatus] = useState<UnitStatus | ''>('');
  const [purpose, setPurpose] = useState<'rent' | 'sale' | ''>('');
  const [unitType, setUnitType] = useState<UnitType | ''>('');
  const [bedrooms, setBedrooms] = useState('');
  const [communityInput, setCommunityInput] = useState('');
  const community = useDebounced(communityInput.trim(), 300);
  const [maxPriceInput, setMaxPriceInput] = useState('');
  const maxPrice = useDebounced(maxPriceInput.trim(), 400);
  const [page, setPage] = useState(1);

  const [units, setUnits] = useState<PropertyUnit[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const reqId = useRef(0);

  // Properties view
  const [properties, setProperties] = useState<PropertyListItem[]>([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsError, setPropsError] = useState(false);
  const propsReq = useRef(0);

  const filtered = !!q || !!status || !!purpose || !!unitType || !!bedrooms || !!community || !!maxPrice;

  const loadSummary = useCallback(() => {
    realEstateService.summary().then(setSummary).catch(() => { /* tiles keep their last numbers */ });
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadUnits = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(false);
    try {
      const r = await realEstateService.listUnits({
        q: q || undefined,
        status: status || undefined,
        purpose: purpose || undefined,
        unitType: unitType || undefined,
        bedrooms: bedrooms || undefined,
        community: community || undefined,
        maxPrice: maxPrice || undefined,
        page,
        limit: LIMIT,
      });
      if (id !== reqId.current) return;
      setUnits(r.items);
      setTotal(r.total);
    } catch {
      if (id === reqId.current) setError(true);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [q, status, purpose, unitType, bedrooms, community, maxPrice, page]);

  const loadProperties = useCallback(async () => {
    const id = ++propsReq.current;
    setPropsLoading(true);
    setPropsError(false);
    try {
      const r = await realEstateService.listProperties(q || undefined);
      if (id === propsReq.current) setProperties(r.items);
    } catch {
      if (id === propsReq.current) setPropsError(true);
    } finally {
      if (id === propsReq.current) setPropsLoading(false);
    }
  }, [q]);

  useEffect(() => { if (view === 'units') void loadUnits(); }, [view, loadUnits]);
  useEffect(() => { if (view === 'properties') void loadProperties(); }, [view, loadProperties]);
  useEffect(() => { setPage(1); }, [q, community, maxPrice]);

  const setView = (v: View) => {
    const p = new URLSearchParams(params);
    if (v === 'units') p.delete('view');
    else p.set('view', v);
    setParams(p, { replace: true });
  };

  const clearFilters = () => {
    setQuery(''); setStatus(''); setPurpose(''); setUnitType(''); setBedrooms(''); setCommunityInput(''); setMaxPriceInput(''); setPage(1);
  };
  const pick = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };

  const empty = summary !== null && summary.properties === 0;
  const priceLabel = purpose === 'sale' ? 'Max price' : 'Max rent';
  const cur = summary?.currency ?? companyCurrency;

  const occupancy = summary?.occupancyPct;
  const tiles = useMemo(() => ([
    { label: 'Units', value: summary?.units, icon: <Layers size={16} />, tone: 'gold' as const, hint: summary ? `${summary.properties} ${summary.properties === 1 ? noun.toLowerCase() : plural.toLowerCase()}` : undefined, filter: '' as const },
    { label: 'Available', value: summary?.available, icon: <DoorOpen size={16} />, tone: 'green' as const, hint: summary?.offMarket ? `${summary.offMarket} off market` : 'Ready to rent or sell', filter: 'available' as const },
    { label: 'Reserved', value: summary?.reserved, icon: <Timer size={16} />, tone: 'amber' as const, hint: 'Held for a client', filter: 'reserved' as const },
    { label: 'Rented', value: summary?.rented, icon: <KeyRound size={16} />, tone: 'blue' as const, hint: occupancy !== null && occupancy !== undefined ? `${occupancy}% of lettable units` : 'Held by an active lease', filter: 'rented' as const },
    { label: 'Sold', value: summary?.sold, icon: <Handshake size={16} />, tone: 'gold' as const, hint: 'Closed sales', filter: 'sold' as const },
  ]), [summary, occupancy, noun, plural]);

  const newProperty = () => setFormOpen(true);

  return (
    <>
      <Topbar
        title={plural}
        breadcrumb={`${t('operations')} / ${plural}`}
        description="Every unit you rent or sell — status, price and who owns it."
        right={<Button iconLeft={<Plus size={15} />} onClick={newProperty}>New {noun.toLowerCase()}</Button>}
      />

      <div className="w-full space-y-5 px-4 py-6 sm:px-7 sm:py-7">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-6">
          {tiles.map((x) => (
            <Kpi
              key={x.label}
              label={x.label}
              value={x.value}
              icon={x.icon}
              tone={x.tone}
              hint={x.hint}
              onClick={() => { setView('units'); setStatus(status === x.filter ? '' : x.filter); setPage(1); }}
              active={view === 'units' && status === x.filter && x.filter !== ''}
            />
          ))}
          <Kpi
            label="Annual rent roll"
            value={summary ? formatPrice(summary.annualRentRoll, cur) : undefined}
            icon={<Banknote size={16} />}
            tone="green"
            hint={summary ? `${summary.activeLeases} active ${(summary.activeLeases === 1 ? t('leases', 'singular') : t('leases')).toLowerCase()} · ${summary.expiringIn60Days} ending ≤60 days` : undefined}
          />
        </div>

        {empty ? (
          <EmptyState
            icon={<Building2 size={28} />}
            title={`No ${plural.toLowerCase()} yet`}
            description="Add a building, compound or villa with its units. A villa is one property with one unit; a tower has many — each with its own price and status."
            action={<Button iconLeft={<Plus size={15} />} onClick={newProperty}>Add your first {noun.toLowerCase()}</Button>}
          />
        ) : (
          <div className="rounded-2xl border border-border bg-surface shadow-soft">
            <div className="flex flex-col gap-3 border-b border-border p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <Segmented<View>
                  value={view}
                  onChange={setView}
                  size="sm"
                  options={[{ value: 'units', label: 'Units' }, { value: 'properties', label: plural }]}
                />
                <div className="w-full lg:max-w-xs">
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={view === 'units' ? 'Unit, reference, building or area' : `Search ${plural.toLowerCase()}`}
                    iconLeft={<Search size={15} />}
                    iconRight={query ? <button type="button" aria-label="Clear search" onClick={() => setQuery('')} className="hover:text-text-primary"><X size={14} /></button> : undefined}
                    aria-label="Search"
                  />
                </div>
                {view === 'units' && (
                  <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                    <Chip active={purpose === ''} onClick={() => pick(setPurpose)('')}>All</Chip>
                    <Chip active={purpose === 'rent'} onClick={() => pick(setPurpose)('rent')}>For rent</Chip>
                    <Chip active={purpose === 'sale'} onClick={() => pick(setPurpose)('sale')}>For sale</Chip>
                  </div>
                )}
              </div>
              {view === 'units' && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
                  <Select value={status} onChange={(e) => pick(setStatus)(e.target.value as UnitStatus | '')} aria-label="Status" className="h-10">
                    <option value="">Any status</option>
                    {UNIT_STATUSES.map((s) => <option key={s} value={s}>{UNIT_STATUS_LABEL[s]}</option>)}
                  </Select>
                  <Select value={unitType} onChange={(e) => pick(setUnitType)(e.target.value as UnitType | '')} aria-label="Unit type" className="h-10">
                    <option value="">Any type</option>
                    {UNIT_TYPES.map((u) => <option key={u} value={u}>{UNIT_TYPE_LABEL[u]}</option>)}
                  </Select>
                  <Select value={bedrooms} onChange={(e) => pick(setBedrooms)(e.target.value)} aria-label="Bedrooms" className="h-10">
                    <option value="">Any bedrooms</option>
                    {BEDROOM_FILTERS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </Select>
                  <Input value={communityInput} onChange={(e) => setCommunityInput(e.target.value)} placeholder="Area or city" aria-label="Area or city" />
                  <Input
                    value={maxPriceInput}
                    onChange={(e) => setMaxPriceInput(e.target.value)}
                    placeholder={`${priceLabel} (e.g. 120k)`}
                    aria-label={priceLabel}
                    inputMode="decimal"
                    title={purpose === 'sale' ? 'Highest sale price' : 'Highest rent — pick “For sale” to filter by sale price'}
                  />
                </div>
              )}
            </div>

            {view === 'units' ? (
              error ? (
                <LoadErrorState what="your units" onRetry={() => void loadUnits()} bare />
              ) : loading && units.length === 0 ? (
                <div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
              ) : units.length === 0 ? (
                <div className="p-4">
                  {filtered ? (
                    <NoMatchesState onClear={clearFilters} />
                  ) : (
                    <EmptyState
                      icon={<Layers size={28} />}
                      title="No units yet"
                      description={`Your ${plural.toLowerCase()} don’t have units. Open one to add the apartments, villas or shops you rent or sell.`}
                      action={<Button variant="secondary" onClick={() => setView('properties')}>See {plural.toLowerCase()}</Button>}
                    />
                  )}
                </div>
              ) : (
                <div className={clsx('transition-opacity', loading && 'opacity-60')}>
                  <div className="hidden grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1fr)_110px_minmax(0,0.8fr)_16px] gap-3 border-b border-border bg-surface-alt px-4 py-2 text-2xs font-semibold uppercase tracking-[0.1em] text-text-muted md:grid">
                    <span>Unit</span><span>{noun}</span><span>Price</span><span>Status</span><span>Owner</span><span />
                  </div>
                  <ul className="divide-y divide-border">
                    {units.map((u) => (
                      <li key={u.unitUid}>
                        <UnitRow unit={u} currency={companyCurrency} onOpen={() => navigate(`/properties/${u.propertyUid}`)} />
                      </li>
                    ))}
                  </ul>
                  {total > LIMIT && <div className="border-t border-border px-4"><Pagination page={page} total={total} limit={LIMIT} onChange={setPage} /></div>}
                </div>
              )
            ) : propsError ? (
              <LoadErrorState what={`your ${plural.toLowerCase()}`} onRetry={() => void loadProperties()} bare />
            ) : propsLoading && properties.length === 0 ? (
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-32 rounded-2xl" />)}</div>
            ) : properties.length === 0 ? (
              <div className="p-4">
                {q ? (
                  <NoMatchesState onClear={clearFilters} />
                ) : (
                  <EmptyState
                    icon={<Building2 size={28} />}
                    title={`No ${plural.toLowerCase()} yet`}
                    description="Add a building, compound or villa with its units."
                    action={<Button iconLeft={<Plus size={15} />} onClick={newProperty}>New {noun.toLowerCase()}</Button>}
                  />
                )}
              </div>
            ) : (
              <div className={clsx('grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3 transition-opacity', propsLoading && 'opacity-60')}>
                {properties.map((p) => <PropertyCard key={p.propertyUid} property={p} />)}
              </div>
            )}
          </div>
        )}
      </div>

      <PropertyFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={(p) => { setFormOpen(false); navigate(`/properties/${p.propertyUid}`); }}
      />
    </>
  );
}

function UnitRow({ unit: u, currency, onOpen }: { unit: PropertyUnit; currency: string; onOpen: () => void }) {
  const prices = unitPrices(u, currency);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      className="grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 px-4 py-3 transition-colors hover:bg-surface-alt/60 focus-visible:bg-surface-alt focus-visible:outline-none md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1fr)_110px_minmax(0,0.8fr)_16px] md:items-center"
    >
      <div className="flex min-w-0 items-center gap-3">
        {u.imageUrls?.[0] ? (
          <img src={u.imageUrls[0]} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 text-primary"><Building2 size={16} strokeWidth={1.6} /></span>
        )}
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-semibold text-text-primary">
            {u.label}{u.reference ? <span className="ml-1.5 text-2xs font-normal text-text-muted">{u.reference}</span> : null}
          </div>
          <div className="truncate text-2xs text-text-muted">{unitSpecs(u)}</div>
        </div>
      </div>
      <div className="justify-self-end md:hidden">
        <StatusPill status={u.status} label={UNIT_STATUS_LABEL[u.status]} variant={UNIT_STATUS_VARIANT[u.status]} />
      </div>
      <div className="col-span-2 min-w-0 md:col-span-1">
        <Link to={`/properties/${u.propertyUid}`} onClick={(e) => e.stopPropagation()} className="block truncate text-[13px] font-medium text-text-primary hover:text-primary hover:underline">
          {u.property?.name ?? 'Property'}
        </Link>
        <div className="truncate text-2xs text-text-muted">{placeLine(u.property) || (u.property ? PROPERTY_TYPE_LABEL[u.property.type] : '')}</div>
      </div>
      <div className="col-span-2 min-w-0 md:col-span-1">
        {prices.map((p) => (
          <div key={p.kind} className={clsx('truncate text-xs tabular-nums', p.missing ? 'text-text-muted' : 'font-semibold text-text-primary')}>{p.text}</div>
        ))}
        <div className="truncate text-2xs text-text-muted">{PURPOSE_LABEL[u.purpose]}{u.availableFrom && u.status === 'available' ? ` · from ${formatDay(u.availableFrom, { day: 'numeric', month: 'short' })}` : ''}</div>
      </div>
      <div className="hidden md:block">
        <StatusPill status={u.status} label={UNIT_STATUS_LABEL[u.status]} variant={UNIT_STATUS_VARIANT[u.status]} />
      </div>
      <div className="col-span-2 min-w-0 truncate text-xs text-text-secondary md:col-span-1">
        {u.ownerName ? <><span className="text-text-muted md:hidden">Owner: </span>{u.ownerName}</> : <span className="text-text-muted">—</span>}
      </div>
      <ChevronRight size={16} className="hidden text-text-muted md:block" />
    </div>
  );
}

function PropertyCard({ property: p }: { property: PropertyListItem }) {
  const c = p.unitCounts;
  const counts: Array<[UnitStatus, number]> = UNIT_STATUSES.map((s): [UnitStatus, number] => [s, c[s] ?? 0]).filter(([, n]) => n > 0);
  return (
    <Link
      to={`/properties/${p.propertyUid}`}
      className="group flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4 transition-all hover:-translate-y-0.5 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="flex items-start gap-3">
        {p.imageUrls?.[0] ? (
          <img src={p.imageUrls[0]} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
        ) : (
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary"><Building2 size={20} strokeWidth={1.6} /></span>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold text-text-primary group-hover:text-primary">{p.name}</div>
          <div className="truncate text-xs text-text-muted">{[PROPERTY_TYPE_LABEL[p.type], placeLine(p)].filter(Boolean).join(' · ')}</div>
          {p.ownerName && <div className="truncate text-2xs text-text-muted">Owner: {p.ownerName}</div>}
        </div>
      </div>
      <div className="mt-auto flex flex-wrap items-center gap-1.5">
        <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-semibold text-text-secondary">{c.total} {c.total === 1 ? 'unit' : 'units'}</span>
        {counts.map(([s, n]) => (
          <span key={s} className="inline-flex items-center gap-1 text-2xs text-text-muted">
            {s === 'available' ? <CheckCircle2 size={11} className="text-success" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            {n} {UNIT_STATUS_LABEL[s].toLowerCase()}
          </span>
        ))}
      </div>
    </Link>
  );
}
